import type { ReactNode } from "react"

import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"

export interface ColumnDef<T> {
  header: ReactNode
  headerClassName?: string
  cell: (row: T) => ReactNode
  cellClassName?: string
  isRowHeader?: boolean
  mobileCard?: (row: T) => ReactNode
}

export interface ResponsiveTableProps<T> {
  columns: ColumnDef<T>[]
  data: T[]
  keyFn: (row: T) => string
  caption?: string
  emptyState?: ReactNode
  rowClassName?: (row: T) => string | undefined
  mobileHeader?: ReactNode
  mobileCardClassName?: string
  className?: string
}

export function ResponsiveTable<T>({
  columns,
  data,
  keyFn,
  caption,
  emptyState,
  rowClassName,
  mobileHeader,
  mobileCardClassName,
  className,
}: ResponsiveTableProps<T>) {
  if (data.length === 0 && emptyState) {
    return <>{emptyState}</>
  }

  const mobileColumns = columns.filter((col) => col.mobileCard != null)

  return (
    <>
      {mobileColumns.length > 0 && (
        <div className={cn("grid gap-3 md:hidden", className)}>
          {mobileHeader}
          {data.map((row) => (
            <div
              key={keyFn(row)}
              className={cn(
                "inline-card bg-card/45 grid gap-3",
                mobileCardClassName,
              )}
            >
              {mobileColumns.map((col, i) => (
                <div key={i}>{col.mobileCard?.(row)}</div>
              ))}
            </div>
          ))}
        </div>
      )}

      <Table className="data-table hidden text-left text-sm md:table">
        {caption ? (
          <TableCaption className="sr-only">{caption}</TableCaption>
        ) : null}
        <TableHeader>
          <TableRow>
            {columns.map((col, i) => (
              <TableHead key={i} scope="col" className={col.headerClassName}>
                {col.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row) => (
            <TableRow key={keyFn(row)} className={rowClassName?.(row)}>
              {columns.map((col, i) => {
                if (col.isRowHeader) {
                  return (
                    <TableHead key={i} scope="row" className="font-medium">
                      {col.cell(row)}
                    </TableHead>
                  )
                }
                return (
                  <TableCell key={i} className={col.cellClassName}>
                    {col.cell(row)}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  )
}
